import type { AccordionItemResolved } from './types';
import { blocksToPlainText } from '@/lib/render/blocksToPlainText';

/**
 * Builds the `FAQPage` block an Accordion emits when its `isFaq` flag is set
 * (10.03.03.02 AC 4).
 *
 * Pure module colocated with the widget, per the widget-owned JSON-LD
 * convention — same shape as `school-details/LocalBusinessJsonLd.ts`.
 *
 * The caller passes `items` followed by `itemsRight`, so the block lists every
 * item of *this* Accordion. Multiple flagged Accordions on a page emit
 * independent blocks; Google merges them.
 */
export function buildFaqPageJsonLd(
  items: AccordionItemResolved[],
  /**
   * Placeholder substitution, e.g. resolves `{location.name}`. This path does
   * not go through `RichText`, which substitutes the rendered body on its own,
   * so without this a raw `{…}` token would end up in the structured data.
   */
  sub: (text: string) => string = (text) => text,
): Record<string, unknown> | null {
  const mainEntity = items
    .map((item) => ({
      name: sub(item.title).trim(),
      text: sub(blocksToPlainText(item.content ?? undefined)),
    }))
    // An item with no question or no answer text is skipped rather than
    // emitted with an empty string — Google treats those as invalid.
    .filter((entry) => entry.name !== '' && entry.text !== '')
    .map((entry) => ({
      '@type': 'Question',
      'name': entry.name,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': entry.text,
      },
    }));

  if (mainEntity.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}
